// Spec 28 PR 1 — OAuthConnectCard.
//
// One big primary Pill that fires the configured connect tool;
// secondary "Maybe later" link. State-machine visuals:
//   idle: full layout.
//   submitting: pill shows spinner (the tool call returns a
//     redirectUrl; the surface navigates to it). After navigation
//     the user lands back on /welcome; the synthetic tool_result
//     drives the transition to succeeded/failed.
//   succeeded: compresses to a single trace line
//     ("✓ GitHub connected").
//   failed: error caption below the body, pill becomes
//     "Try again".
//
// Signature element: the provider name lives in display type, no
// brand icon. The provider's character comes through wording, not
// a logo dropped on the card.

import { ArrowRight, Loader2 } from "lucide-react";

import { Pill } from "@/components/glass";

import type { WidgetRenderProps } from "../registry";

interface OAuthConnectCardProps {
	provider: "github" | "gmail";
	title: string;
	body: string;
	connectLabel: string;
	skipLabel: string | null;
}

const PROVIDER_LABEL: Record<OAuthConnectCardProps["provider"], string> = {
	github: "GitHub",
	gmail: "Gmail",
};

export function OAuthConnectCard({
	props,
	state,
	onAction,
}: WidgetRenderProps<OAuthConnectCardProps>) {
	const submitting = state.kind === "submitting";
	const succeeded = state.kind === "succeeded";
	const failed = state.kind === "failed";

	if (succeeded) {
		// Server emits succeeded for both connect-verify (result.action
		// === "verify") and skip (result.action === "cancel"). Show a
		// neutral "skipped" trace on cancel; a misleading green-check
		// "GitHub connected" was the previous behaviour.
		const r = state.result as { action?: string } | undefined;
		if (r?.action === "cancel") {
			return (
				<div className="text-center py-2">
					<span className="text-[12px] text-[var(--color-text-dim)]">
						Skipped {PROVIDER_LABEL[props.provider]}
					</span>
				</div>
			);
		}
		return (
			<div className="text-center py-2">
				<span className="text-[12px] text-[var(--color-text-muted)]">
					<span className="text-[var(--color-success)] mr-1.5">✓</span>
					{PROVIDER_LABEL[props.provider]} connected
				</span>
			</div>
		);
	}

	return (
		<div
			className="text-center py-10 space-y-6"
			style={{
				opacity: submitting ? 0.8 : 1,
				transition: "opacity 200ms",
			}}
		>
			<div className="space-y-3">
				<div className="text-[24px] font-medium tracking-tight text-[var(--color-text)] anim-fade-rise">
					{props.title}
				</div>
				<div
					className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto leading-relaxed anim-fade-in"
					style={{ animationDelay: "120ms" }}
				>
					{props.body}
				</div>
			</div>

			{failed && (
				<div className="text-[11px] text-[var(--color-warning,#f59e0b)]">
					Connection failed. Try again or skip.
				</div>
			)}

			{submitting && (
				<div className="text-[11px] text-[var(--color-text-muted)] max-w-sm mx-auto">
					Finish installing {PROVIDER_LABEL[props.provider]} in the other
					tab. We'll pick it up as soon as you do.
				</div>
			)}

			<div
				className="flex flex-col items-center gap-3 anim-fade-rise"
				style={{ animationDelay: "240ms" }}
			>
				<Pill
					onClick={() => !submitting && onAction("connect")}
					disabled={submitting}
					className="!h-11 !px-5 !text-[14px]"
				>
					{submitting ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<>
							{failed ? "Try again" : props.connectLabel}
							<ArrowRight className="w-4 h-4" />
						</>
					)}
				</Pill>
				{props.skipLabel && (
					<button
						type="button"
						onClick={() => onAction("cancel")}
						className="text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors"
					>
						{props.skipLabel}
					</button>
				)}
			</div>
		</div>
	);
}
